import { httpService } from './api/http';
import { resolveGatewayBackedServiceBaseUrl } from './serviceBaseUrl';
import { normalizeScheduledEntityFormVisibility } from '../constants/scheduledEntityVisibility';

const CANONICAL_UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const SCHEDULED_OBJECT_TYPES = new Set(['EVENT', 'SESSION']);
const EVENT_ENTITY_TYPES = new Set(['EVENT', 'SESSION', 'VENUE']);
const RSVP_MUTATION_STATUSES = new Set(['GOING', 'INTERESTED', 'DECLINED', 'LEFT']);

function requireCanonicalObjectId(objectId, fieldName = 'objectId') {
    if (typeof objectId !== 'string' || !CANONICAL_UUID_REGEX.test(objectId.trim())) {
        throw new Error(`${fieldName} must be a canonical UUID`);
    }
    return objectId.trim();
}

function requireNormalizedUpperString(value, fieldName) {
    if (typeof value !== 'string' || !value.trim()) {
        throw new Error(`${fieldName} is required`);
    }
    return value.trim().toUpperCase();
}

function requireEntityObjectType(objectType, fieldName = 'objectType') {
    const normalized = requireNormalizedUpperString(objectType, fieldName);
    if (!EVENT_ENTITY_TYPES.has(normalized)) {
        throw new Error(`${fieldName} must be one of EVENT, SESSION, VENUE`);
    }
    return normalized;
}

function requireScheduledObjectType(objectType, fieldName = 'objectType') {
    const normalized = requireNormalizedUpperString(objectType, fieldName);
    if (!SCHEDULED_OBJECT_TYPES.has(normalized)) {
        throw new Error(`${fieldName} must be one of EVENT, SESSION`);
    }
    return normalized;
}

function requireAttendanceStatus(status, fieldName = 'status') {
    const normalized = requireNormalizedUpperString(status, fieldName);
    if (!RSVP_MUTATION_STATUSES.has(normalized)) {
        throw new Error(`${fieldName} must be one of GOING, INTERESTED, DECLINED, LEFT`);
    }
    return normalized;
}

const EVENT_SERVICE_API_BASE = resolveGatewayBackedServiceBaseUrl(
    process.env.REACT_APP_EVENT_SERVICE_URL,
);
const VENUE_SERVICE_API_BASE = resolveGatewayBackedServiceBaseUrl(
    process.env.REACT_APP_VENUE_SERVICE_URL,
);

const EVENT_ENDPOINTS = Object.freeze({
    EVENTS: `${EVENT_SERVICE_API_BASE}/api/events/v1/events`,
    SESSIONS: `${EVENT_SERVICE_API_BASE}/api/events/v1/sessions`,
    BAND_EVENTS: (bandObjectId) => `${EVENT_SERVICE_API_BASE}/api/events/v1/bands/${encodeURIComponent(requireCanonicalObjectId(bandObjectId, 'bandObjectId'))}/events`,
    VENUES: `${VENUE_SERVICE_API_BASE}/api/venues/v1/venues`,
    TIMELINE_FEED: `${EVENT_SERVICE_API_BASE}/api/events/v1/timeline/feed`,
    TIMELINE_USER_ME: `${EVENT_SERVICE_API_BASE}/api/events/v1/timeline/user/me`,
    TIMELINE_USER_BY_ID: (userId) => `${EVENT_SERVICE_API_BASE}/api/events/v1/timeline/user/${encodeURIComponent(requireCanonicalObjectId(userId, 'userId'))}`,
    TIMELINE_PUBLIC_USER_BY_ID: (userId) => `${EVENT_SERVICE_API_BASE}/api/events/v1/timeline/public/users/${encodeURIComponent(requireCanonicalObjectId(userId, 'userId'))}`,
    VENUE_TIMELINE_FEED: `${VENUE_SERVICE_API_BASE}/api/venues/v1/timeline/feed`,
    VENUE_TIMELINE_USER_ME: `${VENUE_SERVICE_API_BASE}/api/venues/v1/timeline/user/me`,
    VENUE_TIMELINE_USER_BY_ID: (userId) => `${VENUE_SERVICE_API_BASE}/api/venues/v1/timeline/user/${encodeURIComponent(requireCanonicalObjectId(userId, 'userId'))}`,
});

function buildIdempotencyKey(operation) {
    const timestamp = Date.now().toString(36);
    const randomSuffix = Math.random().toString(36).slice(2, 12);
    const key = `event-service:${operation}:${timestamp}:${randomSuffix}`;
    return key.slice(0, 128);
}

function withIdempotencyHeader(options = {}, operation) {
    const normalizedOptions = options && typeof options === 'object' ? options : {};
    const existingHeaders = normalizedOptions.headers && typeof normalizedOptions.headers === 'object'
        ? normalizedOptions.headers
        : {};
    const hasIdempotencyHeader = Object.keys(existingHeaders).some(
        (headerName) => headerName.toLowerCase() === 'idempotency-key'
    );
    if (hasIdempotencyHeader) {
        return {
            ...normalizedOptions,
            headers: { ...existingHeaders },
        };
    }
    return {
        ...normalizedOptions,
        headers: {
            ...existingHeaders,
            'Idempotency-Key': buildIdempotencyKey(operation),
        },
    };
}

function resolveEntityCollectionEndpoint(objectType) {
    const normalizedType = requireEntityObjectType(objectType, 'objectType');
    if (normalizedType === 'EVENT') {
        return EVENT_ENDPOINTS.EVENTS;
    }
    if (normalizedType === 'SESSION') {
        return EVENT_ENDPOINTS.SESSIONS;
    }
    return EVENT_ENDPOINTS.VENUES;
}

function resolveScheduledCollectionEndpoint(objectType) {
    const normalizedType = requireScheduledObjectType(objectType, 'objectType');
    return normalizedType === 'EVENT'
        ? EVENT_ENDPOINTS.EVENTS
        : EVENT_ENDPOINTS.SESSIONS;
}

function buildObjectEndpoint(collectionEndpoint, objectId) {
    const normalizedObjectId = requireCanonicalObjectId(objectId, 'objectId');
    return `${collectionEndpoint}/${encodeURIComponent(normalizedObjectId)}`;
}

async function createEvent(payload, options = {}) {
    return httpService.post(EVENT_ENDPOINTS.EVENTS, payload, withIdempotencyHeader(options, 'create-event'));
}

async function updateEvent(objectId, payload, options = {}) {
    const normalizedObjectId = requireCanonicalObjectId(objectId, 'event objectId');
    return httpService.put(`${EVENT_ENDPOINTS.EVENTS}/${encodeURIComponent(normalizedObjectId)}`, payload, options);
}

async function createSession(payload, options = {}) {
    return httpService.post(EVENT_ENDPOINTS.SESSIONS, payload, withIdempotencyHeader(options, 'create-session'));
}

async function updateSession(objectId, payload, options = {}) {
    const normalizedObjectId = requireCanonicalObjectId(objectId, 'session objectId');
    return httpService.put(`${EVENT_ENDPOINTS.SESSIONS}/${encodeURIComponent(normalizedObjectId)}`, payload, options);
}

async function createVenue(payload, options = {}) {
    return httpService.post(EVENT_ENDPOINTS.VENUES, payload, withIdempotencyHeader(options, 'create-venue'));
}

async function updateVenue(objectId, payload, options = {}) {
    const normalizedObjectId = requireCanonicalObjectId(objectId, 'venue objectId');
    return httpService.put(`${EVENT_ENDPOINTS.VENUES}/${encodeURIComponent(normalizedObjectId)}`, payload, options);
}

async function deleteEvent(objectId, options = {}) {
    const normalizedObjectId = requireCanonicalObjectId(objectId, 'event objectId');
    return httpService.delete(`${EVENT_ENDPOINTS.EVENTS}/${encodeURIComponent(normalizedObjectId)}`, options);
}

async function deleteSession(objectId, options = {}) {
    const normalizedObjectId = requireCanonicalObjectId(objectId, 'session objectId');
    return httpService.delete(`${EVENT_ENDPOINTS.SESSIONS}/${encodeURIComponent(normalizedObjectId)}`, options);
}

async function deleteVenue(objectId, options = {}) {
    const normalizedObjectId = requireCanonicalObjectId(objectId, 'venue objectId');
    return httpService.delete(`${EVENT_ENDPOINTS.VENUES}/${encodeURIComponent(normalizedObjectId)}`, options);
}

async function getScheduledEntity(objectType, objectId, options = {}) {
    const collectionEndpoint = resolveScheduledCollectionEndpoint(objectType);
    return httpService.get(buildObjectEndpoint(collectionEndpoint, objectId), options);
}

async function getEvent(objectId, options = {}) {
    return getScheduledEntity('EVENT', objectId, options);
}

async function getBandEvents(bandObjectId, options = {}) {
    return httpService.get(
        withOptionalTimelinePagination(EVENT_ENDPOINTS.BAND_EVENTS(bandObjectId), options),
        options,
    );
}

async function getPublicBandEvents(bandObjectId, options = {}) {
    return getBandEvents(bandObjectId, {
        ...options,
        audience: 'public',
    });
}

async function getSession(objectId, options = {}) {
    return getScheduledEntity('SESSION', objectId, options);
}

async function getVenue(objectId, options = {}) {
    return httpService.get(buildObjectEndpoint(EVENT_ENDPOINTS.VENUES, objectId), options);
}

async function getEntity(objectType, objectId, options = {}) {
    const collectionEndpoint = resolveEntityCollectionEndpoint(objectType);
    return httpService.get(buildObjectEndpoint(collectionEndpoint, objectId), options);
}

async function updateScheduledEntityVisibility(objectType, entity, visibility, options = {}) {
    const collectionEndpoint = resolveScheduledCollectionEndpoint(objectType);
    const normalizedObjectId = requireCanonicalObjectId(entity?.id, 'objectId');
    const normalizedVisibility = normalizeScheduledEntityFormVisibility(visibility);
    return httpService.patch(
        `${collectionEndpoint}/${encodeURIComponent(normalizedObjectId)}/visibility`,
        { visibility: normalizedVisibility },
        options,
    );
}

async function deleteEntity(objectType, objectId, options = {}) {
    const normalizedObjectType = requireEntityObjectType(objectType, 'objectType');
    if (normalizedObjectType === 'EVENT') {
        return deleteEvent(objectId, options);
    }
    if (normalizedObjectType === 'SESSION') {
        return deleteSession(objectId, options);
    }
    return deleteVenue(objectId, options);
}

async function getAttendance(objectType, objectId, options = {}) {
    const collectionEndpoint = resolveScheduledCollectionEndpoint(objectType);
    return httpService.get(`${buildObjectEndpoint(collectionEndpoint, objectId)}/attendance`, options);
}

async function upsertAttendance(objectType, objectId, status, options = {}) {
    const collectionEndpoint = resolveScheduledCollectionEndpoint(objectType);
    const normalizedStatus = requireAttendanceStatus(status, 'status');
    return httpService.post(
        `${buildObjectEndpoint(collectionEndpoint, objectId)}/attendance`,
        { status: normalizedStatus },
        options,
    );
}

async function addScheduledEntityReaction(objectType, objectId, emoji, options = {}) {
    const collectionEndpoint = resolveScheduledCollectionEndpoint(objectType);
    return httpService.post(
        `${buildObjectEndpoint(collectionEndpoint, objectId)}/reactions`,
        { emoji },
        options,
    );
}

async function removeScheduledEntityReaction(objectType, objectId, emoji, options = {}) {
    const collectionEndpoint = resolveScheduledCollectionEndpoint(objectType);
    return httpService.delete(
        `${buildObjectEndpoint(collectionEndpoint, objectId)}/reactions?emoji=${encodeURIComponent(emoji)}`,
        options,
    );
}

async function fetchScheduledEntityReactions(objectType, objectId, options = {}) {
    const collectionEndpoint = resolveScheduledCollectionEndpoint(objectType);
    const reactions = await httpService.get(`${buildObjectEndpoint(collectionEndpoint, objectId)}/reactions`, options);
    return Array.isArray(reactions) ? reactions : [];
}

async function addScheduledEntityComment(objectType, objectId, content, parentId = null, options = {}) {
    const collectionEndpoint = resolveScheduledCollectionEndpoint(objectType);
    return httpService.post(
        `${buildObjectEndpoint(collectionEndpoint, objectId)}/comments`,
        { content, parentId },
        options,
    );
}

async function updateScheduledEntityComment(objectType, objectId, commentId, content, options = {}) {
    const collectionEndpoint = resolveScheduledCollectionEndpoint(objectType);
    const normalizedCommentId = requireCanonicalObjectId(commentId, 'commentId');
    return httpService.patch(
        `${buildObjectEndpoint(collectionEndpoint, objectId)}/comments/${encodeURIComponent(normalizedCommentId)}`,
        { content },
        options,
    );
}

async function deleteScheduledEntityComment(objectType, objectId, commentId, options = {}) {
    const collectionEndpoint = resolveScheduledCollectionEndpoint(objectType);
    const normalizedCommentId = requireCanonicalObjectId(commentId, 'commentId');
    return httpService.delete(
        `${buildObjectEndpoint(collectionEndpoint, objectId)}/comments/${encodeURIComponent(normalizedCommentId)}`,
        options,
    );
}

async function fetchScheduledEntityComments(objectType, objectId, page = 0, size = 20, options = {}) {
    const collectionEndpoint = resolveScheduledCollectionEndpoint(objectType);
    const response = await httpService.get(
        `${buildObjectEndpoint(collectionEndpoint, objectId)}/comments?page=${page}&size=${size}`,
        options,
    );
    return {
        comments: Array.isArray(response?.comments) ? response.comments : [],
        total: typeof response?.total === 'number' ? response.total : 0,
    };
}

function withOptionalTimelinePagination(endpoint, options = {}) {
    const params = new URLSearchParams();
    if (typeof options.limit === 'number' && Number.isFinite(options.limit)) {
        params.set('limit', String(Math.max(1, Math.floor(options.limit))));
    }
    if (typeof options.offset === 'number' && Number.isFinite(options.offset)) {
        params.set('offset', String(Math.max(0, Math.floor(options.offset))));
    }
    if (typeof options.query === 'string' && options.query.trim()) {
        params.set('query', options.query.trim());
    }
    if (options.audience === 'public') {
        params.set('audience', 'public');
    }
    if (options.upcomingOnly === true) {
        params.set('upcomingOnly', 'true');
    }
    const query = params.toString();
    return query ? `${endpoint}?${query}` : endpoint;
}

async function getFeedTimeline(options = {}) {
    return httpService.get(withOptionalTimelinePagination(EVENT_ENDPOINTS.TIMELINE_FEED, options), options);
}

async function getCurrentUserTimeline(options = {}) {
    return httpService.get(withOptionalTimelinePagination(EVENT_ENDPOINTS.TIMELINE_USER_ME, options), options);
}

async function getUserTimeline(userId, options = {}) {
    return httpService.get(
        withOptionalTimelinePagination(EVENT_ENDPOINTS.TIMELINE_USER_BY_ID(userId), options),
        options,
    );
}

async function getPublicUserTimeline(userId, options = {}) {
    return httpService.get(
        withOptionalTimelinePagination(EVENT_ENDPOINTS.TIMELINE_PUBLIC_USER_BY_ID(userId), options),
        options,
    );
}

async function getVenueFeedTimeline(options = {}) {
    return httpService.get(withOptionalTimelinePagination(EVENT_ENDPOINTS.VENUE_TIMELINE_FEED, options), options);
}

async function getCurrentUserVenueTimeline(options = {}) {
    return httpService.get(withOptionalTimelinePagination(EVENT_ENDPOINTS.VENUE_TIMELINE_USER_ME, options), options);
}

async function getUserVenueTimeline(userId, options = {}) {
    return httpService.get(
        withOptionalTimelinePagination(EVENT_ENDPOINTS.VENUE_TIMELINE_USER_BY_ID(userId), options),
        options,
    );
}

export const eventService = Object.freeze({
    endpoints: EVENT_ENDPOINTS,
    createEvent,
    updateEvent,
    createSession,
    updateSession,
    createVenue,
    updateVenue,
    deleteEvent,
    deleteSession,
    deleteVenue,
    deleteEntity,
    getEntity,
    updateScheduledEntityVisibility,
    getEvent,
    getBandEvents,
    getPublicBandEvents,
    getSession,
    getVenue,
    getScheduledEntity,
    getAttendance,
    upsertAttendance,
    addScheduledEntityReaction,
    removeScheduledEntityReaction,
    fetchScheduledEntityReactions,
    addScheduledEntityComment,
    updateScheduledEntityComment,
    deleteScheduledEntityComment,
    fetchScheduledEntityComments,
    getFeedTimeline,
    getCurrentUserTimeline,
    getUserTimeline,
    getPublicUserTimeline,
    getVenueFeedTimeline,
    getCurrentUserVenueTimeline,
    getUserVenueTimeline,
});
